import React, { useState } from 'react';
import { CheckIcon, CopyIcon, SendIcon, XIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Field } from '../ui/Field';
import { TextInput, SelectControl } from '../ui/TextControl';
import { CURRENT_MEMBER } from '../../data/community';
import { YEAR_OPTIONS } from '../../data/registration';
interface InviteDialogProps {
  open: boolean;
  onClose: () => void;
}
const HOW_OPTIONS = [{
  label: 'We were here at the same time',
  value: 'same-time'
}, {
  label: 'They worked or volunteered here',
  value: 'staff'
}, {
  label: 'They were in my group',
  value: 'group'
}, {
  label: 'Another way',
  value: 'other'
}];

/**
 * A named, personal invitation. The recipient lands on a page that says who
 * thought of them, which is the only reason this audience opens the email.
 */
export function InviteDialog({
  open,
  onClose
}: InviteDialogProps) {
  const [name, setName] = useState('');
  const [how, setHow] = useState('same-time');
  const [year, setYear] = useState('');
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!open) return null;
  const firstName = name.trim().split(' ')[0];
  return <div role="dialog" aria-modal="true" aria-label="Invite someone" className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8" data-id="element-136" data-mp="2ggtxwj81c9nh">
      <button type="button" aria-hidden="true" tabIndex={-1} onClick={onClose} className="fixed inset-0 cursor-default bg-ink opacity-80" data-id="element-137" data-mp="2cddwl9l0tl4g" />

      <div className="relative w-full max-w-lg overflow-hidden rounded-card border border-rule bg-card shadow-2xl" data-id="element-138" data-mp="1jogkjo7ekkk5">
        <div className="flex items-start justify-between gap-4 border-b border-rule px-5 py-4" data-id="element-139" data-mp="1sr9cltoet0b1">
          <div className="flex flex-col" data-id="element-140" data-mp="151nd1544kj2i">
            <h2 className="text-section-heading font-semibold text-ink" data-id="element-141" data-mp="150lqa3dqy84y">
              {sent ? 'Invitation sent' : 'Invite someone you remember'}
            </h2>
            {!sent ? <p className="text-small text-ink-muted" data-id="element-142" data-mp="305lcsgwus92p">
                They&apos;ll see that it came from you, and nothing else about you.
              </p> : null}
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-pill text-ink-muted hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-143" data-mp="39ud0gcuvs77t">
            <span className="sr-only" data-id="element-144" data-mp="2m5kbx1gl91n8">Close</span>
            <XIcon aria-hidden="true" className="h-5 w-5" data-id="element-145" data-mp="2r4erzvn68c3n" />
          </button>
        </div>

        {sent ? <div className="flex flex-col gap-4 px-5 py-6" data-id="element-146" data-mp="2w3rh6n1ilv9e">
            <p className="text-body text-ink" data-id="element-147" data-mp="2w158ul6svbq4">
              {firstName || 'They'} will get one email. If they don&apos;t answer it, we
              won&apos;t send another, and you won&apos;t be told either way.
            </p>
            <div className="rounded-field bg-canvas p-4" data-id="element-148" data-mp="1vurycy2bniuk">
              <p className="text-eyebrow-caption font-semibold uppercase text-ink-muted" data-id="element-149" data-mp="1vu8lhvrj3kne">
                What they&apos;ll see
              </p>
              <p className="mt-2 text-body text-ink" data-id="element-150" data-mp="1m4ows9h4wwkm">
                “{CURRENT_MEMBER.publicName} thinks you were here around the same time
                {year ? ` — around ${year}` : ''}.”
              </p>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-151" data-mp="1c7tztv0if48g">
              <Button variant="secondary" onClick={() => {
            setSent(false);
            setName('');
            setYear('');
          }} data-id="element-152" data-mp="185gxlgkz1ogf">
                Invite someone else
              </Button>
              <Button variant="quiet" onClick={onClose} data-id="element-153" data-mp="y9dy6ihi83x6">
                Done
              </Button>
            </div>
          </div> : <div className="flex flex-col gap-5 px-5 py-5" data-id="element-154" data-mp="2sl11yuidjepp">
            <Field name="inviteName" label="Their name" hint="However you knew them." data-id="element-155" data-mp="2sjhkx1ow8zmv">
              <TextInput id="inviteName" value={name} placeholder="Theresa" onChange={event => setName(event.target.value)} data-id="element-156" data-mp="1q5s0wizy0y4f" />
            </Field>

            <Field name="inviteEmail" label="Their email" data-id="element-157" data-mp="1l8jh6dz5cm12">
              <TextInput id="inviteEmail" type="email" placeholder="name@example.com" data-id="element-158" data-mp="1l6ymmocu1337" />
            </Field>

            <Field name="inviteHow" label="How you knew them" data-id="element-159" data-mp="16hspd5xk1mxy">
              <SelectControl id="inviteHow" value={how} options={HOW_OPTIONS} onChange={event => setHow(event.target.value)} data-id="element-160" data-mp="1bbnggufhp472" />
            </Field>

            <Field name="inviteYear" label="Roughly what year" hint="Optional. It helps them place you." data-id="element-161" data-mp="12ijxk6m2rz51">
              <SelectControl id="inviteYear" value={year} placeholder="Not sure" options={YEAR_OPTIONS} onChange={event => setYear(event.target.value)} data-id="element-162" data-mp="skdwfr83ex18" />
            </Field>

            <div className="rounded-field bg-canvas p-4" data-id="element-163" data-mp="iwidjqofcqz8">
              <p className="text-eyebrow-caption font-semibold uppercase text-ink-muted" data-id="element-164" data-mp="iy36xcz063vi">
                What they&apos;ll see
              </p>
              <p className="mt-2 text-body text-ink" data-id="element-165" data-mp="rwyfuwtfls2i">
                “{CURRENT_MEMBER.publicName} thinks you were here around the same time
                {year ? ` — around ${year}` : ''}.”
              </p>
              <p className="mt-2 text-small text-ink-muted" data-id="element-166" data-mp="11nj904qc64e1">
                Not your surname, not your contact details, and nothing about why you were here.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t border-rule pt-4" data-id="element-167" data-mp="1bknyadfhafz2">
              <Button onClick={() => setSent(true)} data-id="element-168" data-mp="16mcno0hd01fs">
                <SendIcon aria-hidden="true" className="h-4 w-4" data-id="element-169" data-mp="16nnp3tnc0x3q" />
                Send the invitation
              </Button>
              <Button variant="secondary" onClick={() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
          }} data-id="element-170" data-mp="1dng91l8y7o9x">
                {copied ? <>
                    <CheckIcon aria-hidden="true" className="h-4 w-4" data-id="element-171" data-mp="1sfi4mt3zjv45" />
                    Link copied
                  </> : <>
                    <CopyIcon aria-hidden="true" className="h-4 w-4" data-id="element-172" data-mp="21du3fif09z7o" />
                    Copy a link instead
                  </>}
              </Button>
            </div>
          </div>}
      </div>
    </div>;
}